const BACKEND_BASE_URL = String(process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001').trim().replace(/\/$/, '');
const CACHE_STORAGE_KEY = 'plynote:stripe:subscription-plans:v1';
const CACHE_TTL_MS = 5 * 60 * 1000;

const getPreferredCurrency = () => {
  const lang = String(typeof navigator !== 'undefined' ? (navigator.language || '') : '').toLowerCase();
  if (lang.startsWith('fr') || lang.includes('-fr') || lang.includes('_fr')) return 'eur';
  const euroLocales = ['de', 'es', 'it', 'nl', 'pt', 'pl', 'be', 'at', 'ie', 'fi', 'sk', 'si', 'ee', 'lv', 'lt', 'lu', 'mt', 'cy'];
  const prefix = lang.slice(0, 2);
  if (euroLocales.includes(prefix)) return 'eur';
  return 'usd';
};

let memoryCache = {
	plans: null,
	fetchedAt: 0,
};

const isFresh = (fetchedAt) => {
	return Number.isFinite(fetchedAt) && fetchedAt > 0 && (Date.now() - fetchedAt) < CACHE_TTL_MS;
};

const readStorageCache = () => {
	if (typeof window === 'undefined' || !window.sessionStorage) return null;

	try {
		const raw = window.sessionStorage.getItem(CACHE_STORAGE_KEY);
		if (!raw) return null;

		const parsed = JSON.parse(raw);
		const plans = Array.isArray(parsed?.plans) ? parsed.plans : null;
		const fetchedAt = Number(parsed?.fetchedAt || 0);

		if (!plans || !isFresh(fetchedAt)) return null;

		return { plans, fetchedAt };
	} catch (_error) {
		return null;
	}
};

const writeStorageCache = (plans, fetchedAt) => {
	if (typeof window === 'undefined' || !window.sessionStorage) return;

	try {
		window.sessionStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify({ plans, fetchedAt }));
	} catch (_error) {
		// Ignore storage write failures.
	}
};

const updateCaches = (plans, fetchedAt = Date.now()) => {
	memoryCache = { plans, fetchedAt };
	writeStorageCache(plans, fetchedAt);
	return memoryCache;
};

const normalizePlansPayload = (payload) => {
	if (!Array.isArray(payload?.plans)) return [];
	return payload.plans;
};

const fetchStripePlans = async () => {
	const currency = getPreferredCurrency();
	const response = await fetch(`${BACKEND_BASE_URL}/api/payment/subscription-plans?currency=${currency}`);
	const raw = await response.text();

	let payload = {};
	try {
		payload = raw ? JSON.parse(raw) : {};
	} catch (_error) {
		throw new Error('Invalid pricing response from server.');
	}

	if (!response.ok) {
		throw new Error(payload.error || 'Unable to load Stripe plans.');
	}

	const plans = normalizePlansPayload(payload);
	updateCaches(plans);
	return plans;
};

export const getCachedStripePlans = () => {
	if (Array.isArray(memoryCache.plans) && isFresh(memoryCache.fetchedAt)) {
		return memoryCache.plans;
	}

	const fromStorage = readStorageCache();
	if (fromStorage) {
		memoryCache = fromStorage;
		return fromStorage.plans;
	}

	return [];
};

export const prefetchStripePlans = async () => {
	const cached = getCachedStripePlans();
	if (cached.length > 0) return cached;
	return fetchStripePlans();
};

export const loadStripePlans = async ({ forceRefresh = false } = {}) => {
	if (!forceRefresh) {
		const cached = getCachedStripePlans();
		if (cached.length > 0) return cached;
	}

	return fetchStripePlans();
};
